<template>
  <div class="share-container">
    <!-- 顶部导航栏 -->
    <title-bar-back title="分享" @back="back">
      <div class="title_right"></div>
    </title-bar-back>

    <!-- 顶部 Tab 栏（与小程序一致，只保留“商品规格”Tab） -->
    <div class="tab-header">
      <van-tabs
        v-model="activeTab"
        color="#bf9264"
        line-height="3px"
        line-width="16px"
        title-inactive-color="rgba(0,0,0,0.65)"
        title-active-color="#bf9264"
        class="custom-tabs"
      >
        <van-tab title="商品规格" name="size"></van-tab>
      </van-tabs>
    </div>

    <!-- 海报展示区 -->
    <div class="poster-section">
      <div v-if="loading" class="loading-wrapper">
        <van-loading size="36px" vertical color="#bf9264"
          >海报生成中...</van-loading
        >
      </div>
      <div v-else class="poster-preview">
        <img :src="posterImg" alt="商品规格海报" class="poster-img" />
        <!-- <p class="save-tip">长按海报图片即可保存至相册哦</p> -->
      </div>
    </div>

    <!-- 底部控制与操作区域 -->
    <div class="share-footer" v-if="detailData">
      <!-- 价格开关和规格列表并排 -->
      <div class="skus-list-wrap">
        <div class="price-switch">
          <van-switch
            v-model="showPrice"
            size="20px"
            active-color="#000000"
            inactive-color="#eeeeee"
            @change="onPriceSwitchChange"
          />
          <span class="show-price-tip">显示价格</span>
        </div>
        <div class="skus-list">
          <div
            v-for="(item, index) in skuList"
            :key="index"
            :class="['skus-item', { active: index === selectIndex }]"
            @click="switchSku(index)"
          >
            <img :src="item.thumbnailImg || item.masterImg" class="sku-thumb" />
            <span class="sku-name">{{ item.name }}</span>
          </div>
        </div>
      </div>

      <!-- 底部操作按钮（与小程序一致：圆形浅灰底图标+下方文字） -->
      <div class="share-footer-btns">
        <div class="share-btn" @click="saveImage">
          <img :src="iconSaveUrl" class="btn-icon" />
          <span class="btn-text">保存海报</span>
        </div>
        <div class="share-btn" @click="shareImage">
          <img :src="iconShareUrl" class="btn-icon" />
          <span class="btn-text">微信分享</span>
        </div>
        <div class="share-btn" @click="copyLink">
          <img :src="iconLinkUrl" class="btn-icon" />
          <span class="btn-text">{{
            $appSource === 3 ? "复制链接" : "复制短链"
          }}</span>
        </div>
      </div>
    </div>

    <!-- 隐藏的 Canvas 绘制画布 -->
    <canvas
      ref="shareCanvas"
      width="750"
      height="1200"
      class="hidden-canvas"
    ></canvas>

    <!-- 微信分享蒙层 -->
    <share-mask v-if="showShareMask" @hideMask="showShareMask = false" />
  </div>
</template>

<script>
import { fillRoundRect, circleImg, drawRoundRectPath } from "@/utils/canvas";
import { Tab, Tabs, Loading } from "vant";
const TitleBarBack = () =>
  import("@/components/TitleBarBack").then(m => m.default || m);
const ShareMask = () =>
  import("@/views/mine/cardvoucher/components/ShareMask").then(
    m => m.default || m
  );

export default {
  name: "ProductShare",
  components: {
    TitleBarBack,
    ShareMask,
    [Tab.name]: Tab,
    [Tabs.name]: Tabs,
    [Loading.name]: Loading
  },
  data() {
    return {
      activeTab: "size",
      spuId: "",
      selectIndex: -1,
      showPrice: true,
      loading: true,
      posterImg: "",
      detailData: null,
      skuList: [],
      showShareMask: false
    };
  },
  computed: {
    // 渲染海报的数据
    drawData() {
      if (!this.detailData) return {};
      const selectIndex = this.selectIndex;
      if (selectIndex === -1) {
        return {
          mainPic: this.detailData.spuImg,
          finalPrice: this.detailData.clientPrice,
          name: this.detailData.name,
          couponShareSkuAmount: this.detailData.couponShareSkuAmount,
          couponProductList: this.detailData.couponProductList,
          shortName: this.detailData.shortName
        };
      } else {
        const skuItem = this.skuList[selectIndex];
        if (skuItem) {
          const sku = skuItem.sku || skuItem;
          return {
            mainPic:
              sku.masterImg || skuItem.thumbnailImg || this.detailData.spuImg,
            finalPrice: sku.clientPrice,
            name: sku.name,
            couponShareSkuAmount: sku.couponShareSkuAmount,
            couponProductList: sku.couponProductList,
            shortName: this.detailData.shortName
          };
        }
      }
      return {};
    },
    // 圆圈小图标的 本地 路径
    iconSaveUrl() {
      return require("@/assets/common/icon_save_blue.png");
    },
    iconShareUrl() {
      return require("@/assets/common/icon_share_wx.png");
    },
    iconLinkUrl() {
      return require("@/assets/common/icon_short_link.png");
    }
  },
  created() {
    this.spuId = this.$route.query.id || "";
    if (this.spuId) {
      this.fetchProductDetail();
    } else {
      this.$toast("无效的商品ID");
      this.$router.back();
    }
  },
  methods: {
    back() {
      this.$router.back();
    },
    // 获取商品详情数据
    fetchProductDetail() {
      this.loading = true;
      const params = {
        spuId: this.spuId,
        slug: this.$route.query.slug || ""
      };
      this.$store
        .dispatch("product/mallretailspu", { params })
        .then(() => {
          const res = this.$store.getters.mallretailspu;
          if (res) {
            this.detailData = res;
            // 反转 skuUrl，与小程序顺序保持一致
            if (Array.isArray(res.skuUrl)) {
              this.skuList = res.skuUrl.slice().reverse();
            } else {
              this.skuList = [];
            }

            // 默认不选中任何 sku
            this.selectIndex = -1;

            this.$nextTick(() => {
              this.drawPoster();
            });
          } else {
            this.$toast("商品数据加载失败");
            this.loading = false;
          }
        })
        .catch(err => {
          console.error(err);
          this.$toast("商品详情请求失败");
          this.loading = false;
        });
    },
    // 切换规格
    switchSku(index) {
      if (this.selectIndex === index) return;
      this.selectIndex = index;
      this.$nextTick(() => {
        this.drawPoster();
      });
    },
    // 开关价格
    onPriceSwitchChange() {
      this.$nextTick(() => {
        this.drawPoster();
      });
    },
    // 二维码及详情页面链接
    getDetailUrl() {
      let url = `${window.location.origin}/#/product/detail?id=${this.spuId}`;
      const activeSku = this.skuList[this.selectIndex];
      if (activeSku && activeSku.sku) {
        url += `&skuId=${activeSku.sku.id}`;
      }
      if (this.$route.query.slug) {
        url += `&slug=${this.$route.query.slug}`;
      }
      return url;
    },
    getQrCodeUrl() {
      if (this.detailData && this.detailData.miniturl) {
        return `/api/daemon/wxmini/getSpuSuncodeUrl?miniturl=${encodeURIComponent(
          this.detailData.miniturl,
        )}`;
      }
      // 兼容没有 miniturl 的情况，依然使用普通的二维码
      const detailUrl = this.getDetailUrl();
      return `/api/daemon/files/createQrCode?content=${encodeURIComponent(
        detailUrl,
      )}&height=200&width=200`;
    },
    // 资源路径配置
    getXcxBaseUrlImg() {
      const base =
        this.$appSource === 1
          ? "https://assets-img.mkyun.com/assets/xiaochengxu/images/icon"
          : "https://assets-img.mkyun.com/assets/xiaochengxu/azplaceholder";
      return base;
    },
    // 异步加载图片对象
    loadImage(url) {
      return new Promise(resolve => {
        if (!url) {
          resolve(null);
          return;
        }
        const img = new Image();
        img.setAttribute("crossOrigin", "Anonymous");
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        // 对非 base64 路径附加时间戳防缓存
        if (url.indexOf("data:") !== 0) {
          if (url.indexOf("?") > -1) {
            img.src = url; // 如果已有参数（如 OSS 签名），则不附加时间戳以防破坏签名
          } else {
            const stamp = "t=" + new Date().getTime();
            img.src = `${url}?${stamp}`;
          }
        } else {
          img.src = url;
        }
      });
    },
    // 多行文本换行绘制辅助
    drawTextLines(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
      if (!text) return;
      ctx.save();
      ctx.textBaseline = "top";
      ctx.font = "26px Microsoft YaHei";
      ctx.fillStyle = "#888";
      const words = text.split("");
      let line = "";
      const lines = [];
      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i];
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth) {
          lines.push(line);
          line = words[i];
        } else {
          line = testLine;
        }
      }
      lines.push(line);

      for (let i = 0; i < Math.min(lines.length, maxLines); i++) {
        let displayText = lines[i];
        if (i === maxLines - 1 && lines.length > maxLines) {
          while (
            ctx.measureText(displayText + "...").width > maxWidth &&
            displayText.length > 0
          ) {
            displayText = displayText.slice(0, -1);
          }
          displayText += "...";
        }
        ctx.fillText(displayText, x, y + i * lineHeight);
      }
      ctx.restore();
    },
    // Canvas 绘制海报
    async drawPoster() {
      this.loading = true;
      const canvas = this.$refs.shareCanvas;
      if (!canvas || !canvas.getContext) {
        this.loading = false;
        return;
      }
      const ctx = canvas.getContext("2d");

      // 准备海报需要绘制的数据
      const data = this.drawData;
      const storeLogo = this.detailData.popStoreInfo?.brandLogo || "";
      const storeName = this.detailData.popStoreInfo?.storeName || "";
      const slogan = this.detailData.popStoreInfo?.slogan || "";
      const bgImgUrl = `${this.getXcxBaseUrlImg()}/pic_share_bg.png`;
      const qrcodeUrl = this.getQrCodeUrl();

      // 并发异步加载所有图片资源
      const [mainPicImg, storeLogoImg, bgImg, qrcodeImg] = await Promise.all([
        this.loadImage(data.mainPic),
        this.loadImage(storeLogo),
        this.loadImage(bgImgUrl),
        this.loadImage(qrcodeUrl)
      ]);

      // 绘制逻辑开始，虚拟尺寸 375x600，乘2后的物理尺寸为 750x1200
      ctx.clearRect(0, 0, 750, 1200);

      // 1. 绘制背景渐变
      const grad = ctx.createLinearGradient(0, 0, 0, 1200);
      grad.addColorStop(0, "#e3d6bd");
      grad.addColorStop(1, "#f7f0e2");
      fillRoundRect(ctx, 0, 0, 750, 1200, 16, grad);

      // 2. 绘制顶部背景图
      if (bgImg) {
        ctx.drawImage(bgImg, 102, 0, 648, 140);
      }

      // 3. 绘制中央白色圆角卡片
      fillRoundRect(ctx, 30, 140, 690, 960, 20, "#ffffff");

      // 4. 绘制商品主图 (带圆角裁剪，圆角 8px 对应物理 16px)
      if (mainPicImg) {
        ctx.save();
        ctx.translate(60, 170);
        drawRoundRectPath(ctx, 630, 630, 16);
        ctx.clip();
        ctx.drawImage(mainPicImg, 0, 0, 630, 630);
        ctx.restore();
      }

      // 5. 绘制店铺 Logo
      if (storeLogoImg) {
        circleImg(ctx, storeLogoImg, 32, 32, 80, 80, 40);
      }

      // 6. 绘制店铺名
      if (storeName) {
        ctx.save();
        ctx.textBaseline = "top";
        ctx.font = "bold 36px Microsoft YaHei";
        ctx.fillStyle = "#614e2a";
        ctx.fillText(storeName, 144, 32);
        ctx.restore();
      }

      // 7. 绘制店标口号
      if (slogan) {
        ctx.save();
        ctx.textBaseline = "top";
        ctx.font = "28px Microsoft YaHei";
        ctx.fillStyle = "#ae9563";
        ctx.fillText(slogan, 144, 84);
        ctx.restore();
      }

      // 8. 绘制二维码
      if (qrcodeImg) {
        ctx.drawImage(qrcodeImg, 480, 840, 212, 212);
      }

      // 9. 绘制底部保障描述
      ctx.save();
      ctx.textBaseline = "top";
      ctx.font = "30px Microsoft YaHei";
      ctx.fillStyle = "#dcccaf";
      ctx.fillText("全球好货", 84, 1140);
      ctx.fillText("优质服务", 320, 1140);
      ctx.fillText("简单生活", 550, 1140);
      ctx.restore();

      // 10. 绘制价格和折扣文本
      const reduceHeight = this.showPrice ? 0 : 80;
      if (this.showPrice) {
        if (data.couponShareSkuAmount) {
          // 券后价模式
          ctx.save();
          ctx.textBaseline = "top";
          ctx.font = "bold 36px PingFang SC,Arial";
          ctx.fillStyle = "#000";
          const priceText = `${this.$G("symbol")}${parseFloat(
            data.finalPrice
          ).toFixed(2)}`;
          ctx.fillText(priceText, 60, 830);
          const priceWidth = ctx.measureText(priceText).width;
          ctx.restore();

          ctx.save();
          ctx.textBaseline = "top";
          ctx.font = "20px PingFang SC,Arial";
          ctx.fillStyle = "#888";
          ctx.fillText("券后价", 80 + priceWidth, 836);
          ctx.restore();

          ctx.save();
          ctx.textBaseline = "top";
          ctx.font = "bold 36px PingFang SC,Arial";
          ctx.fillStyle = "#f82d38";
          ctx.fillText(
            `${this.$G("symbol")}${parseFloat(
              data.couponShareSkuAmount
            ).toFixed(2)}`,
            60,
            880
          );
          ctx.restore();
        } else {
          // 普通售价模式
          ctx.save();
          ctx.textBaseline = "top";
          ctx.font = "bold 40px PingFang SC,Arial";
          ctx.fillStyle = "#000";
          ctx.fillText(
            `${this.$G("symbol")}${parseFloat(data.finalPrice).toFixed(2)}`,
            60,
            848
          );
          ctx.restore();
        }
      }

      // 11. 绘制优惠券标签与商品文本
      if (data.couponProductList && data.couponProductList.length > 0) {
        const item = data.couponProductList[0];
        // 绘制“券”字背景红标
        fillRoundRect(ctx, 60, 920 - reduceHeight, 28, 28, 4, "#fd3c6a");
        ctx.save();
        ctx.font = "18px PingFang SC,Arial";
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("券", 74, 934 - reduceHeight);
        ctx.restore();

        // 绘制优惠详情
        ctx.save();
        ctx.font = "18px PingFang SC,Arial";
        const couponMsg = item.couponMessageStr;
        const textWidth = ctx.measureText(couponMsg).width;
        fillRoundRect(
          ctx,
          96,
          920 - reduceHeight,
          textWidth + 12,
          28,
          4,
          "#fde6ee"
        );
        ctx.fillStyle = "#fd3c6a";
        ctx.textBaseline = "top";
        ctx.fillText(couponMsg, 102, 925 - reduceHeight);
        ctx.restore();

        // 绘制商品短名
        ctx.save();
        ctx.textBaseline = "top";
        ctx.font = "bold 32px Microsoft YaHei";
        ctx.fillStyle = "#000";
        ctx.fillText(data.shortName || "", 60, 964 - reduceHeight);
        ctx.restore();

        // 绘制商品全名
        this.drawTextLines(ctx, data.name, 60, 1020 - reduceHeight, 400, 32, 2);
      } else {
        // 无优惠券
        ctx.save();
        ctx.textBaseline = "top";
        ctx.font = "bold 32px Microsoft YaHei";
        ctx.fillStyle = "#000";
        ctx.fillText(data.shortName || "", 60, 950 - reduceHeight);
        ctx.restore();

        this.drawTextLines(ctx, data.name, 60, 1010 - reduceHeight, 400, 32, 2);
      }

      // 12. 导出图片并赋给 Image 显示
      try {
        const base64Img = canvas.toDataURL("image/jpeg", 1.0);
        this.posterImg = base64Img;
      } catch (err) {
        console.error("生成海报图片失败：", err);
        this.$toast("绘制海报发生错误");
      } finally {
        this.loading = false;
      }
    },
    // 保存海报
    saveImage() {
      if (!this.posterImg) return;
      const link = document.createElement("a");
      link.href = this.posterImg;
      link.download = "商品规格海报.png";
      link.click();
    },
    // 微信分享
    shareImage() {
      const ua = navigator.userAgent.toLowerCase();
      const isWechat = ua.indexOf("micromessenger") !== -1;
      if (isWechat) {
        this.showShareMask = true;
      } else {
        this.$toast("请在微信中打开页面进行分享");
      }
    },
    // 复制链接
    copyLink() {
      const url = this.detailData.h5url;
      this.$copyText(url)
        .then(() => {
          this.$toast("链接复制成功");
        })
        .catch(() => {
          this.$toast("复制链接失败，请手动复制");
        });
    }
  }
};
</script>

<style lang="less" scoped>
.share-container {
  min-height: 100vh;
  background-color: #f7f8fa;
  display: flex;
  flex-direction: column;

  .nav-bar {
    background-color: #ffffff;
    border-bottom: 1px solid #f1f2f3;
    ::v-deep .van-nav-bar__title {
      font-weight: 500;
      font-size: 16px;
    }
  }

  .tab-header {
    background-color: #ffffff;
    border-bottom: 1px solid #f5f6f8;

    .custom-tabs {
      ::v-deep .van-tabs__wrap {
        height: 44px;
      }
      ::v-deep .van-tab {
        font-weight: 500;
        font-size: 14px;
        flex: none;
        padding: 0 20px;
      }
    }
  }

  .poster-section {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;

    .loading-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 250px;
    }

    .poster-preview {
      display: flex;
      flex-direction: column;
      align-items: center;

      .poster-img {
        width: 320px;
        height: 512px;
        border-radius: 8px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
        object-fit: cover;
      }

      .save-tip {
        margin-top: 12px;
        font-size: 12px;
        color: #999;
        font-weight: 400;
      }
    }
  }

  .share-footer {
    background-color: #ffffff;
    box-shadow: 0 -3px 10px rgba(0, 0, 0, 0.04);
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom));

    .skus-list-wrap {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      border-bottom: 1px solid #f5f6f8;
      padding-bottom: 8px;

      .price-switch {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-right: 16px;
        flex-shrink: 0;

        .show-price-tip {
          font-size: 10px;
          color: #bf9264;
          margin-top: 4px;
          font-weight: 400;
        }
      }

      .skus-list {
        display: flex;
        overflow-x: auto;
        flex-grow: 1;
        gap: 8px;
        padding: 2px 0;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }

        .skus-item {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          background: #f5f5f5;
          border-radius: 16px;
          padding: 4px 10px;
          border: 1px solid transparent;
          transition: all 0.2s ease;

          .sku-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            object-fit: cover;
            margin-right: 6px;
          }

          .sku-name {
            color: #666666;
            font-size: 11px;
            white-space: nowrap;
          }

          &.active {
            background-color: #ffffff;
            border-color: #bf9264;

            .sku-name {
              color: #bf9264;
              font-weight: 500;
            }
          }
        }
      }
    }

    .share-footer-btns {
      display: flex;
      justify-content: space-around;
      padding: 4px 0 4px;

      .share-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        transition: opacity 0.2s;

        &:active {
          opacity: 0.7;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          margin-bottom: 4px;
          object-fit: contain;
        }

        .btn-text {
          font-size: 11px;
          color: #333333;
          font-weight: 400;
        }
      }
    }
  }

  .hidden-canvas {
    position: fixed;
    top: -9999px;
    left: -9999px;
    visibility: hidden;
  }
}
</style>
